import type { Attempt } from '../types'
import { overallScore } from '../lib/assessment'
import { buildLearningTargets, getDueReviews, labelForTarget, queueMirrorSprint, weeklyStudySeconds } from '../lib/learning'
import { MISCONCEPTIONS } from '../types'
import { Metric } from '../components/Metric'

const minutes = (seconds: number) => `${Math.max(0, Math.round(seconds / 60))} phút`

export function Dashboard({ attempts, go }: { attempts: Attempt[]; go?: (route: string) => void }) {
  const targets = buildLearningTargets(attempts)
  const due = getDueReviews(attempts)
  const score = overallScore(attempts)
  const weeklySeconds = weeklyStudySeconds(attempts)
  const highConfidenceMisses = attempts.filter(a => !a.correct && a.confidence === 'high').length
  const mastered = targets.filter(target => target.reviewStage >= 3).length

  const startRepair = (target: typeof targets[number]) => {
    queueMirrorSprint(target)
    go?.('practice')
  }

  if (!attempts.length) {
    return (
      <div className="container dashboard-page">
        <section className="dashboard-hero card text-center">
          <span className="tag-pill orange">ĐƯỜNG THOÁT ĐIỂM</span>
          <h1>Chưa đủ dữ liệu để đoán — hãy đo thật.</h1>
          <p className="text-muted">Làm 8–10 câu đầu. Sau đó hệ thống sẽ chỉ ra lỗi nào đang lặp lại, nguyên nhân nào đáng sửa trước và câu nào nên ôn lại.</p>
          <button className="btn btn-primary" onClick={() => go?.('practice')}>Bắt đầu đo năng lực</button>
        </section>
      </div>
    )
  }

  return (
    <div className="container dashboard-page">
      <section className="dashboard-hero card">
        <div>
          <span className="tag-pill orange">ĐƯỜNG THOÁT ĐIỂM</span>
          <h1>Đừng luyện nhiều hơn. Hãy đóng đúng lỗi đang làm bạn mất điểm.</h1>
          <p className="text-muted">Ưu tiên dưới đây được tính từ câu sai, mức tự tin, số lần lặp lỗi và lịch ôn của chính bạn — không dùng điểm minh hoạ.</p>
        </div>
        <div className="dashboard-hero-callout">
          <span>Việc cần làm ngay</span>
          <strong>{due.length ? `Ôn ${due.length} kỹ năng đến hạn` : targets[0] ? labelForTarget(targets[0]) : 'Làm thêm câu để đo chính xác hơn'}</strong>
          {targets[0] && <button className="btn btn-primary" onClick={() => startRepair(due[0] ?? targets[0])}>Luyện 3 câu sửa lỗi</button>}
        </div>
      </section>

      <div className="features-grid mb-8">
        <Metric label="Độ chính xác thực tế" value={`${score}%`} note={`${attempts.length} lượt trả lời đã lưu`} />
        <Metric label="Học 7 ngày gần đây" value={minutes(weeklySeconds)} note="Từ thời gian làm câu" />
        <Metric label="Lỗi tự tin cao" value={highConfidenceMisses} note="Cần kiểm tra lại cách tự đánh giá" />
        <Metric label="Kỹ năng đã qua 3 mốc" value={mastered} note="Đúng ở các lượt ôn khác nhau" />
      </div>

      <section className="learning-roadmap card">
        <div className="section-heading">
          <div>
            <h2>Ba đường sửa lỗi có tác động lớn nhất</h2>
            <p className="text-muted">Một Mirror Sprint gồm 3 câu cùng năng lực, ưu tiên bối cảnh khác để kiểm tra bạn hiểu bản chất.</p>
          </div>
          {due.length > 0 && <span className="tag-pill orange">{due.length} lượt ôn đến hạn</span>}
        </div>
        <div className="recovery-list">
          {targets.slice(0, 3).map((target, index) => (
            <article className="recovery-card" key={target.key}>
              <div className="recovery-index">{index + 1}</div>
              <div className="recovery-content">
                <div className="flex justify-between gap-3">
                  <div>
                    <h3>{labelForTarget(target)}</h3>
                    <p className="text-muted">{target.topic} · {target.incorrect}/{target.total} câu sai · độ chính xác {target.accuracy}%</p>
                  </div>
                  <span className="tag-pill">Mốc {target.reviewStage}/3</span>
                </div>
                <p className="recovery-reason">
                  {target.primaryMisconception
                    ? `Lỗi lặp lại nhiều nhất: ${MISCONCEPTIONS[target.primaryMisconception]}.`
                    : 'Hãy làm một lượt ngắn để hệ thống xác định nguyên nhân sai cụ thể hơn.'}
                  {target.highConfidenceMisses > 0 && ` Có ${target.highConfidenceMisses} câu sai khi bạn chọn “Chắc chắn”.`}
                </p>
                <div className="flex justify-between items-center gap-3 flex-wrap">
                  <small className="text-muted">
                    {target.nextReviewAt
                      ? `Lịch ôn: ${new Date(target.nextReviewAt).toLocaleDateString('vi-VN')}`
                      : target.reviewStage >= 3 ? 'Đã đóng vòng lỗi hiện tại.' : 'Chưa có lịch ôn — hãy bắt đầu sửa lỗi.'}
                  </small>
                  <button className="btn btn-primary" onClick={() => startRepair(target)}>Sửa bằng 3 câu</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="card calibration-card">
        <h2>Đọc đúng tín hiệu, không tự trách bản thân</h2>
        <div className="grid grid-3 gap-4">
          <div>
            <strong>Câu sai do kiến thức</strong>
            <p className="text-muted">Quay về điều kiện tiên quyết, rồi làm lại bằng dữ kiện mới.</p>
          </div>
          <div>
            <strong>Câu sai do chiến lược</strong>
            <p className="text-muted">Giữ kiến thức, đổi sơ đồ suy luận hoặc thứ tự xử lý dữ kiện.</p>
          </div>
          <div>
            <strong>Câu sai do áp lực thời gian</strong>
            <p className="text-muted">Luyện ngắn theo giới hạn thời gian, chỉ sau khi bạn đã giải đúng không áp lực.</p>
          </div>
        </div>
      </section>
    </div>
  )
}
